/**
 * Hook for playing notification sounds and showing browser notifications
 * when new bookings/applications arrive. Respects user preferences.
 */
import { useRef, useCallback, useEffect } from 'react';
import { loadNotificationPreferences, isNotificationEnabled } from '../components/owner/NotificationPreferences';

// Web Audio API-based notification sound generator
const createNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    return {
      playDing: () => {
        // Create oscillator for the "ding" sound
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // High pitched "ding" 
        oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
        oscillator.frequency.setValueAtTime(1320, audioContext.currentTime + 0.1); // E6
        
        oscillator.type = 'sine';
        
        // Quick fade in and out
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.4);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      },
      
      playUrgent: () => {
        // Two-tone alert for urgent items
        const oscillator1 = audioContext.createOscillator();
        const oscillator2 = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator1.connect(gainNode);
        oscillator2.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator1.frequency.setValueAtTime(523, audioContext.currentTime); // C5
        oscillator2.frequency.setValueAtTime(659, audioContext.currentTime); // E5
        
        oscillator1.type = 'sine';
        oscillator2.type = 'sine';
        
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.05);
        gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.15);
        gainNode.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.25);
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);
        
        oscillator1.start(audioContext.currentTime);
        oscillator2.start(audioContext.currentTime);
        oscillator1.stop(audioContext.currentTime + 0.6);
        oscillator2.stop(audioContext.currentTime + 0.6);
      },
      
      playSuccess: () => {
        // Pleasant success chime
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // Ascending notes
        oscillator.frequency.setValueAtTime(523, audioContext.currentTime); // C5
        oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.15); // E5
        oscillator.frequency.setValueAtTime(784, audioContext.currentTime + 0.3); // G5
        
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.25, audioContext.currentTime + 0.05);
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.6);
      },
      
      playPayment: () => {
        // Cash register "cha-ching" sound
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(1047, audioContext.currentTime); // C6
        oscillator.frequency.setValueAtTime(1319, audioContext.currentTime + 0.1); // E6
        oscillator.frequency.setValueAtTime(1568, audioContext.currentTime + 0.2); // G6
        
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.02);
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.4);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      }
    };
  } catch (e) {
    console.warn('Web Audio API not supported:', e);
    return {
      playDing: () => {},
      playUrgent: () => {},
      playSuccess: () => {},
      playPayment: () => {}
    };
  }
};

// Browser notification helper
const showBrowserNotification = (title, body, tag = 'default') => {
  // Check if browser supports notifications
  if (!('Notification' in window)) {
    return;
  }
  
  // Check if permission is granted
  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: `nc-help-hub-${tag}-${Date.now()}`,
      requireInteraction: false,
      silent: true // We handle sound separately
    });
    
    // Auto-close after 5 seconds
    setTimeout(() => notification.close(), 5000);
    
    // Focus window when clicked
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission();
  }
};

export const useNotificationSound = () => {
  const soundRef = useRef(null);
  const permissionRequested = useRef(false);
  
  // Request notification permission on mount
  useEffect(() => {
    if (!permissionRequested.current && 'Notification' in window && Notification.permission === 'default') {
      permissionRequested.current = true;
      setTimeout(() => {
        Notification.requestPermission().then(permission => {
          console.log('Notification permission:', permission);
        });
      }, 3000);
    }
  }, []);
  
  const initSound = useCallback(() => {
    if (!soundRef.current) {
      soundRef.current = createNotificationSound();
    }
    return soundRef.current;
  }, []);
  
  const playNewBooking = useCallback(() => {
    const prefs = loadNotificationPreferences();
    
    // Play sound if enabled
    if (prefs.soundEnabled && prefs.soundNewBooking) {
      const sound = initSound();
      sound.playDing();
    }
    
    // Show browser notification if tab not focused and enabled
    if (document.hidden && prefs.browserEnabled && prefs.browserNewBooking) {
      showBrowserNotification(
        '🔔 New Booking!',
        'A new booking has been received. Click to view.',
        'booking'
      );
    }
    
    // Return whether toast should show
    return prefs.toastEnabled && prefs.toastNewBooking;
  }, [initSound]);
  
  const playNewApplication = useCallback(() => {
    const prefs = loadNotificationPreferences();
    
    if (prefs.soundEnabled && prefs.soundNewApplication) {
      const sound = initSound();
      sound.playUrgent();
    }
    
    if (document.hidden && prefs.browserEnabled && prefs.browserNewApplication) {
      showBrowserNotification(
        '📋 New Application!',
        'A new worker/driver application has been submitted.',
        'application'
      );
    }
    
    return prefs.toastEnabled && prefs.toastNewApplication;
  }, [initSound]);
  
  const playSuccess = useCallback(() => {
    const prefs = loadNotificationPreferences();
    if (prefs.soundEnabled) {
      const sound = initSound();
      sound.playSuccess();
    }
  }, [initSound]);
  
  const playNewRide = useCallback(() => {
    const prefs = loadNotificationPreferences();
    
    if (prefs.soundEnabled && prefs.soundNewRide) {
      const sound = initSound();
      sound.playDing();
    }
    
    if (document.hidden && prefs.browserEnabled && prefs.browserNewRide) {
      showBrowserNotification(
        '🚗 New Ride Request!',
        'A customer has requested a ride. Click to assign a driver.',
        'ride'
      );
    }
    
    return prefs.toastEnabled && prefs.toastNewRide;
  }, [initSound]);
  
  const playPayment = useCallback(() => {
    const prefs = loadNotificationPreferences();
    
    if (prefs.soundEnabled && prefs.soundPayment) {
      const sound = initSound();
      sound.playPayment();
    }
    
    if (document.hidden && prefs.browserEnabled && prefs.browserPayment) {
      showBrowserNotification(
        '💰 Payment Received!',
        'A payment has been processed successfully.',
        'payment'
      );
    }
    
    return prefs.toastEnabled && prefs.toastPayment;
  }, [initSound]);
  
  const playUrgent = useCallback(() => {
    const prefs = loadNotificationPreferences();
    
    if (prefs.soundEnabled && prefs.soundUrgent) {
      const sound = initSound();
      sound.playUrgent();
    }
    
    if (document.hidden && prefs.browserEnabled && prefs.browserUrgent) {
      showBrowserNotification(
        '⚠️ Urgent Alert!',
        'An urgent matter requires your attention.',
        'urgent'
      );
    }
    
    return prefs.toastEnabled && prefs.toastUrgent;
  }, [initSound]);
  
  // Manual notification request
  const requestPermission = useCallback(async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }, []);
  
  // Check if notifications are enabled
  const notificationsEnabled = useCallback(() => {
    return 'Notification' in window && Notification.permission === 'granted';
  }, []);
  
  // Get current preferences
  const getPreferences = useCallback(() => {
    return loadNotificationPreferences();
  }, []);
  
  return {
    playNewBooking,
    playNewApplication,
    playSuccess,
    playNewRide,
    playPayment,
    playUrgent,
    requestPermission,
    notificationsEnabled,
    getPreferences
  };
};

export default useNotificationSound;
